import React, { useEffect, useState } from "react";
import { getSiteConfig } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Mail, Phone, GraduationCap, Award, BookOpen, Briefcase, ExternalLink, FlaskConical } from "lucide-react";
import DOMPurify from "dompurify";

export default function DirectorPage() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    getSiteConfig("directeur").then(r => setCfg(r.data?.data || {})).catch(() => {});
  }, []); // getSiteConfig is a stable module-level import

  if (!cfg) return <div className="min-h-screen bg-[#FAFAFA]"><Navbar /><div className="pt-32 flex justify-center"><div className="animate-spin w-8 h-8 border-3 border-[#1670B9] border-t-transparent rounded-full" /></div></div>;

  return (
    <div data-testid="director-page" className="bg-[#FAFAFA] min-h-screen">
      <Navbar />
      <div className="bg-gradient-to-br from-[#1670B9] via-[#2d8fd4] to-[#74CDD9] pt-32 pb-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center gap-8">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
            className="w-40 h-40 md:w-52 md:h-52 rounded-2xl overflow-hidden border-4 border-white/30 shadow-xl flex-shrink-0">
            {cfg.photo_url ? (
              <img src={cfg.photo_url} alt={cfg.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-white/10 flex items-center justify-center"><GraduationCap className="w-16 h-16 text-white/50" /></div>
            )}
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-white/60 mb-2">Responsable de l'UR-EBioReZ</p>
            <h1 className="font-['Quicksand'] text-3xl sm:text-4xl font-medium text-white mb-2" data-testid="director-name">{cfg.name}</h1>
            <p className="text-base text-white/80 mb-4">{cfg.title}</p>
            <div className="flex flex-wrap gap-3">
              {cfg.email && (
                <a href={`mailto:${cfg.email}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 text-white/90 text-sm hover:bg-white/20 transition-colors">
                  <Mail className="w-4 h-4" /> {cfg.email}
                </a>
              )}
              {cfg.researchgate && (
                <a href={cfg.researchgate} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 text-white/90 text-sm hover:bg-white/20 transition-colors">
                  <ExternalLink className="w-4 h-4" /> ResearchGate
                </a>
              )}
              {cfg.google_scholar && (
                <a href={cfg.google_scholar} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 text-white/90 text-sm hover:bg-white/20 transition-colors">
                  <ExternalLink className="w-4 h-4" /> Google Scholar
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 space-y-12">
        {/* Bio */}
        {cfg.bio && (
          <section>
            <h2 className="font-['Quicksand'] text-2xl font-semibold text-slate-800 mb-4 flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-[#1670B9]" /> Biographie
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">{cfg.bio}</p>
          </section>
        )}

        {/* Academic Path */}
        {cfg.academic_path && (
          <section>
            <h2 className="font-['Quicksand'] text-2xl font-semibold text-slate-800 mb-4 flex items-center gap-3">
              <GraduationCap className="w-5 h-5 text-[#74CDD9]" /> Parcours académique
            </h2>
            <div className="bg-white rounded-xl p-6 border border-slate-100 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cfg.academic_path) }} />
          </section>
        )}

        {/* Expertise */}
        {cfg.expertise?.length > 0 && (
          <section>
            <h2 className="font-['Quicksand'] text-2xl font-semibold text-slate-800 mb-4 flex items-center gap-3">
              <FlaskConical className="w-5 h-5 text-[#F49C03]" /> Domaines d'expertise
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {cfg.expertise.map((e, i) => (
                <div key={`expertise-${e.slice(0,20)}-${i}`} className="flex items-center gap-3 bg-white rounded-xl p-4 border border-slate-100">
                  <div className="w-2 h-2 rounded-full bg-[#1670B9] flex-shrink-0" />
                  <span className="text-sm text-slate-700">{e}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Responsibilities */}
        {cfg.responsibilities?.length > 0 && (
          <section>
            <h2 className="font-['Quicksand'] text-2xl font-semibold text-slate-800 mb-4 flex items-center gap-3">
              <Briefcase className="w-5 h-5 text-[#1670B9]" /> Responsabilités
            </h2>
            <div className="space-y-2">
              {cfg.responsibilities.map((r, i) => (
                <div key={`resp-${r.slice(0,20)}-${i}`} className="flex items-center gap-3 bg-white rounded-xl p-4 border border-slate-100">
                  <div className="w-2 h-2 rounded-full bg-[#74CDD9] flex-shrink-0" />
                  <span className="text-sm text-slate-700">{r}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Distinctions */}
        {cfg.distinctions?.length > 0 && (
          <section>
            <h2 className="font-['Quicksand'] text-2xl font-semibold text-slate-800 mb-4 flex items-center gap-3">
              <Award className="w-5 h-5 text-[#F49C03]" /> Distinctions
            </h2>
            <div className="space-y-2">
              {cfg.distinctions.map((d, i) => (
                <div key={`dist-${d.slice(0,20)}-${i}`} className="bg-white rounded-xl p-4 border border-slate-100 text-sm text-slate-700">{d}</div>
              ))}
            </div>
          </section>
        )}
      </div>
      <Footer />
    </div>
  );
}
